import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthenticationService } from '../core/services/authentication.service';
import { LogCategory, LoggerService, LogService, LogType } from '../core/services/logger.service';

@Injectable()
export class PeopleService {

    private _network$: Observable<any> = null;           // the user's network 
    private _academicAreas$: Observable<any> = null;    // a list of academic areas used for searching faculty
    private _reload$ = new Subject<void>();              // used to trigger resubscribe/reload for network 
    private _subNewUser: Subscription = null;            // new user subscription for spoof/unspoof events

    public sectionGroups$: Observable<any[]> = null;
    public network$: Observable<any[]> = null;

    public groupsSubject$: BehaviorSubject<boolean> = new BehaviorSubject(true);
    public groups$ = this.groupsSubject$.pipe(
            switchMap((val) => this.http.get<any[]>(environment.apiUrl + '/people/groups')
        )
    )

    public get fullNetwork$(): Observable<any> {
        if (!this._network$) {
            this._network$ = this.http.get<any>(environment.apiUrl + '/people/networkPeopleLists').pipe(
                tap(_ => this.log('network$', 'Fetched network', LogCategory.SubscriptionData)),
                takeUntil(this._reload$),
                shareReplay(1)
            );
        }
        return this._network$;
    }

    public get academicAreas$(): Observable<any> {
        if (!this._academicAreas$) {
            this._academicAreas$ = this.http.get<any>(environment.apiUrl + '/people/academicareas').pipe(
                tap(_ => this.log('academicareas$', 'Fetched academic areas', LogCategory.SubscriptionData)),
                takeUntil(this._reload$),
                shareReplay(1)
            );            
        }
        return this._academicAreas$;
    }

    public typeaheadQuery: string;
    public get people(): any {
        if (!this.typeaheadQuery)
            return of([]);

        const url = `${environment.apiUrl}/people/typeahead`;
        let params = new HttpParams();
        params = params.append('q', this.typeaheadQuery);

        return this.http.get<any>(url, { params: params });
    }

    //  Used for the new person typeahead.  Available parameters:
    //  query (string) REQUIRED - may optionally include additional filters
    //  personTypeList (string, comma delimited list)
    //  excludeExpired (bool)
    //  isSolarWinds (bool)
    public personTypeaheadList$(query: string): Observable<any[]> {
        if (!query?.trim())
            return of([]);

        return this.http.get(`${environment.apiUrl}/people/search?q=${encodeURI(query)}`).pipe(
            //tap(val => console.log('╔═ personTypeaheadList$', val)),
            map((input: any[]) => {
                let retVal = input.map(item => ({
                    name: item.DisplayNameInternal,                 // REQUIRED name
                    desc: item.Description,                         // optional description 
                    img: `${environment.apiUrl}/profile/${item.PersonId}/photo`, // optional image 
                    personId: item.PersonId,
                    pid: item.Pid,                                  // Needed for spoofing
                    emailAddress: item.EmailAddress,
                    isActive: item.IsActive,  //  Needed for shared UI element to make it clear we are showing inactive users
                    isFerpaDataPrivate: item.IsFERPADataPrivate, // adding in case we need to make some UI adjustments
                }),
                );

                return retVal;
            })
        );
    }
    constructor(
        private http: HttpClient,
        private logger: LoggerService,
        private auth: AuthenticationService,
    ) {
        this.log('constructor', null, LogCategory.Constructor);
        this._subNewUser = auth.loggedInUser$.subscribe(() => {
            this.update();
        })

        this.sectionGroups$ = this.fullNetwork$.pipe(map((data) => data.sectionGroups));
        this.network$ = this.fullNetwork$.pipe(map((data) => data.people));
    }
    private log(method: string = null, message = null, category: LogCategory = LogCategory.Default): void {
        method = (method) ? '.' + method + '()' : '';
        message = (message) ? message.toString() : '';
        this.logger.try(this.constructor.name + method + ': ' + message, LogType.Service, LogService.People, category);
    }

    public update() {

        // Update the profile by calling the API again
        this.log("update");
        this._network$ = null; // set to null first because that's checked when reload re-emits 
        this._reload$.next();

        // Use the property to cause it to try to fetch 
        var v = this._network$;
    }
    private _lookups$: Observable<any>
    public searchLookups$() {
        const url = `${environment.apiUrl}/people/searchlookups`;
        if (this._lookups$ == null)
            this._lookups$ = this.http.get<any>(url);

        return this._lookups$;
    }

    public advancedProfileSearch$(searchCriteria: PeopleSearchCriteria) {
        const url = `${environment.apiUrl}/people/advancedsearch`;       
        return this.http.post<any>(url, searchCriteria);
    }

    // Finds a person by name, up to X number of results   
    public lookupByName$(partialName: string, max: number) {

        const url = `${environment.apiUrl}/people/lookup?query=${partialName}&max=${max}`;

        // No need to unsubscribe from finite observables like http 
        return this.http.get<any>(url);
    }

    private loadGroups() {
        this.groupsSubject$.next(true);
    }

    // Adds or updates an affinity group 
    public updateGroup(groupName: string, selected: number[]): void {
        const url = `${environment.apiUrl}/people/groups`;
        this.http.post<any>(url, { groupName, selected })
            .subscribe(
                () => {
                    this.log("updateGroup", "updated group", LogCategory.SubscriptionData);
                    this.loadGroups(); // Refresh groups after update
                },
                error => this.log("updateGroup", error, LogCategory.SubscriptionData),
            );
    }

    // Removes a person from an affinity group
    public deleteFromGroup(groupId: number, personId: number): void {
        const url = `${environment.apiUrl}/people/group/${groupId}/${personId}`;
        this.http.delete<any>(url)
            .subscribe(
                () => {
                    this.log("deleteFromGroup", `removed person ${personId} from group ${groupId}`, LogCategory.SubscriptionData);
                    this.loadGroups(); // Refresh groups after delete
                },
                error => this.log("deleteFromGroup", error, LogCategory.SubscriptionData),
            );
    }

    // Delete affinity group
    public deleteGroup(groupId: number): void {
        const url = `${environment.apiUrl}/people/group/${groupId}`;
        this.http.delete<any>(url)
            .subscribe(
                () => {
                    this.log("deleteGroup", `removed group ${groupId}`, LogCategory.SubscriptionData);
                    this.loadGroups(); // Refresh groups after delete
                },
                error => this.log("deleteGroup", error, LogCategory.SubscriptionData),
            );
    }

}

export class PeopleSearchCriteria {
    
    public constructor(
        public searchTerm: string,
        public searchType: string,
        public program: string[],
        public classOf: number,
        public cohort: string,
        public legacy: string,
        public entryYear: string,
        public classification: string,
        public exchange: number,
        public academicArea: string,
        public division: string) {}

}
